// Shared JSON-LD builders for editorial indexes, calculators and corporate pages.
// Output is a `scripts` array suitable for `head()`'s scripts field.

const SITE = "https://drhint.com";
const ORG = {
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "DrHint",
  url: `${SITE}/`,
  logo: `${SITE}/og-image.png`,
};

type Script = { type: "application/ld+json"; children: string };
const s = (obj: unknown): Script => ({ type: "application/ld+json", children: JSON.stringify(obj) });

function breadcrumb(trail: { name: string; item: string }[]): Script {
  return s({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t.name, item: t.item })),
  });
}

// ── Editorial index pages ──────────────────────────────────────────────────
export function buildCollectionLd(opts: {
  url: string;
  name: string;
  description: string;
  items: { url: string; name: string; description?: string; datePublished?: string }[];
  trail: { name: string; item: string }[];
}): Script[] {
  return [
    s({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": opts.url,
      url: opts.url,
      name: opts.name,
      description: opts.description,
      isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website` },
      publisher: ORG,
      hasPart: opts.items.slice(0, 50).map((it) => ({
        "@type": "CreativeWork",
        url: it.url,
        name: it.name,
        ...(it.description ? { description: it.description } : {}),
        ...(it.datePublished ? { datePublished: it.datePublished } : {}),
      })),
    }),
    s({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: opts.name,
      numberOfItems: opts.items.length,
      itemListElement: opts.items.slice(0, 100).map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: it.url,
        name: it.name,
      })),
    }),
    breadcrumb(opts.trail),
  ];
}

// ── Calculator / tool pages ────────────────────────────────────────────────
export function buildCalculatorLd(opts: {
  url: string;
  name: string;
  description: string;
  howTo: { name: string; steps: { name: string; text: string }[]; totalTime?: string };
  faqs: { q: string; a: string }[];
  trail: { name: string; item: string }[];
  featureList?: string[];
  applicationCategory?: string;
  priceCurrency?: string;
}): Script[] {
  return [
    s({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: opts.name,
      url: opts.url,
      applicationCategory: opts.applicationCategory ?? "UtilitiesApplication",
      operatingSystem: "Any (web browser)",
      isAccessibleForFree: true,
      offers: { "@type": "Offer", price: "0", priceCurrency: opts.priceCurrency ?? "USD" },
      ...(opts.featureList ? { featureList: opts.featureList } : {}),
      publisher: ORG,
    }),
    s({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: opts.howTo.name,
      description: opts.description,
      ...(opts.howTo.totalTime ? { totalTime: opts.howTo.totalTime } : {}),
      step: opts.howTo.steps.map((st, i) => ({ "@type": "HowToStep", position: i + 1, name: st.name, text: st.text, url: `${opts.url}#step-${i + 1}` })),
    }),
    s({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: opts.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    }),
    breadcrumb(opts.trail),
  ];
}

// ── About / Contact / Legal ────────────────────────────────────────────────
export function buildAboutLd(opts: { url: string; name: string; description: string; trail: { name: string; item: string }[] }): Script[] {
  return [
    s({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": opts.url,
      url: opts.url,
      name: opts.name,
      description: opts.description,
      isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website` },
      about: {
        "@type": "Organization",
        "@id": `${SITE}/#organization`,
        name: "DrHint",
        url: `${SITE}/`,
        foundingDate: "2024",
        founder: { "@type": "Person", name: "DrHint Editorial Team" },
        knowsAbout: ["Live gold rate", "Live silver rate", "Pakistan utility bills", "Loan EMI calculation"],
        areaServed: [
          { "@type": "Country", name: "Pakistan" },
          { "@type": "Country", name: "India" },
          { "@type": "Country", name: "United Arab Emirates" },
          { "@type": "Country", name: "Saudi Arabia" },
        ],
      },
    }),
    breadcrumb(opts.trail),
  ];
}

export function buildContactLd(opts: { url: string; name: string; description: string; email?: string; trail: { name: string; item: string }[] }): Script[] {
  return [
    s({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": opts.url,
      url: opts.url,
      name: opts.name,
      description: opts.description,
      isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website` },
      mainEntity: {
        "@type": "Organization",
        "@id": `${SITE}/#organization`,
        name: "DrHint",
        url: `${SITE}/`,
        contactPoint: opts.email
          ? [{ "@type": "ContactPoint", contactType: "customer support", email: opts.email, availableLanguage: ["English", "Urdu", "Hindi"], areaServed: ["PK", "IN", "AE", "SA"] }]
          : undefined,
      },
    }),
    breadcrumb(opts.trail),
  ];
}

export function buildLegalLd(opts: { url: string; name: string; description: string; lastModified: string; trail: { name: string; item: string }[] }): Script[] {
  return [
    s({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": opts.url,
      url: opts.url,
      name: opts.name,
      description: opts.description,
      
      isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website` },
      publisher: ORG,
    }),
    breadcrumb(opts.trail),
  ];
}